import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Plus, Pencil, Trash2, Check, X } from 'lucide-react';
import {
  useValidationRules,
  useCreateValidationRule,
  useUpdateValidationRule,
  useDeleteValidationRule,
} from '../../hooks/useValidationRules.js';
import { toast } from '../../lib/toast.js';
import { useFeatureFlag } from '../../lib/posthog.js';
import type { ValidationRule } from '@mayksel/types';
import type { CreateValidationRuleInput } from '@mayksel/validators';

const ENTITY_TABS = ['company', 'contact', 'bucket', 'order', 'line_item'] as const;
type EntityTab = typeof ENTITY_TABS[number];

const TRIGGERS = ['on_create', 'on_update', 'on_stage_change'] as const;
const OPERATORS = ['equals', 'not_equals', 'is_blank', 'is_not_blank', 'gt', 'lt'] as const;
const ACTION_TYPES = ['require_field', 'prevent_save', 'show_warning'] as const;

const OPERATORS_WITHOUT_VALUE = new Set(['is_blank', 'is_not_blank']);
const ACTION_TYPES_WITH_FIELD = new Set(['require_field']);

interface RuleFormState {
  name: string;
  trigger: string;
  conditionField: string;
  conditionOperator: string;
  conditionValue: string;
  actionType: string;
  actionField: string;
  errorMessage: string;
}

function emptyForm(): RuleFormState {
  return {
    name: '',
    trigger: 'on_create',
    conditionField: '',
    conditionOperator: 'equals',
    conditionValue: '',
    actionType: 'require_field',
    actionField: '',
    errorMessage: '',
  };
}

function ruleToForm(rule: ValidationRule): RuleFormState {
  return {
    name: rule.name,
    trigger: rule.trigger,
    conditionField: rule.condition.field,
    conditionOperator: rule.condition.operator,
    conditionValue: rule.condition.value != null ? String(rule.condition.value) : '',
    actionType: rule.action.type,
    actionField: rule.action.field ?? '',
    errorMessage: rule.errorMessage,
  };
}

function formToInput(form: RuleFormState, entityType: EntityTab): CreateValidationRuleInput {
  const condition: Record<string, unknown> = {
    field: form.conditionField,
    operator: form.conditionOperator,
  };
  if (!OPERATORS_WITHOUT_VALUE.has(form.conditionOperator)) {
    condition.value = form.conditionValue;
  }

  const action: Record<string, unknown> = { type: form.actionType };
  if (ACTION_TYPES_WITH_FIELD.has(form.actionType) && form.actionField) {
    action.field = form.actionField;
  }

  return {
    entityType,
    name: form.name.trim(),
    trigger: form.trigger as CreateValidationRuleInput['trigger'],
    condition: condition as CreateValidationRuleInput['condition'],
    action: action as CreateValidationRuleInput['action'],
    errorMessage: form.errorMessage.trim(),
    isActive: true,
  };
}

export default function ValidationRulesSettings() {
  const { t } = useTranslation('validationRules');
  const { t: ts } = useTranslation('settings');
  const validationRulesEnabled = useFeatureFlag('validation_rules');

  const [activeTab, setActiveTab] = useState<EntityTab>('order');
  const { data: rules = [], isLoading, isError } = useValidationRules(activeTab);

  const createRule = useCreateValidationRule();
  const updateRule = useUpdateValidationRule();
  const deleteRule = useDeleteValidationRule();

  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState<RuleFormState>(emptyForm);
  const [editingId, setEditingId] = useState<string | null>(null);

  if (!validationRulesEnabled) return null;

  function setField<K extends keyof RuleFormState>(key: K, value: RuleFormState[K]) {
    setForm((prev) => ({ ...prev, [key]: value }));
  }

  function startCreate() {
    setForm(emptyForm());
    setEditingId(null);
    setShowForm(true);
  }

  function startEdit(rule: ValidationRule) {
    setForm(ruleToForm(rule));
    setEditingId(rule.id);
    setShowForm(true);
  }

  function cancelForm() {
    setShowForm(false);
    setEditingId(null);
  }

  async function handleSave() {
    if (!form.name.trim() || !form.conditionField.trim() || !form.errorMessage.trim()) return;

    const input = formToInput(form, activeTab);

    try {
      if (editingId) {
        await updateRule.mutateAsync({ id: editingId, data: input });
      } else {
        await createRule.mutateAsync(input);
      }
      setShowForm(false);
      setEditingId(null);
    } catch {
      toast.error(editingId ? t('save_error') : t('create_error'));
    }
  }

  async function handleDelete(rule: ValidationRule) {
    if (!confirm(t('delete_confirm', { name: rule.name }))) return;
    try {
      await deleteRule.mutateAsync(rule.id);
    } catch {
      toast.error(t('delete_error'));
    }
  }

  async function handleToggleActive(rule: ValidationRule) {
    try {
      await updateRule.mutateAsync({ id: rule.id, data: { isActive: !rule.isActive } });
    } catch {
      toast.error(t('save_error'));
    }
  }

  const isSaving = createRule.isPending || updateRule.isPending;
  const showValueInput = !OPERATORS_WITHOUT_VALUE.has(form.conditionOperator);
  const showActionField = ACTION_TYPES_WITH_FIELD.has(form.actionType);

  return (
    <div>
      {/* Header */}
      <div style={{ marginBottom: 'var(--space-6)' }}>
        <h2 style={{ fontSize: 'var(--text-xl)', fontWeight: 'var(--font-medium)', color: 'var(--color-text)', marginBottom: 'var(--space-1)' }}>
          {t('title')}
        </h2>
        <p style={{ fontSize: 'var(--text-sm)', color: 'var(--color-text-muted)' }}>
          {t('subtitle')}
        </p>
      </div>

      {/* Entity tabs */}
      <div style={{ display: 'flex', gap: 0, marginBottom: 'var(--space-4)', borderBottom: '1px solid var(--color-border)' }}>
        {ENTITY_TABS.map((tab) => (
          <button
            key={tab}
            type="button"
            onClick={() => { setActiveTab(tab); setShowForm(false); }}
            style={{
              padding: 'var(--space-2) var(--space-4)',
              fontSize: 'var(--text-sm)',
              fontWeight: activeTab === tab ? 'var(--font-medium)' : undefined,
              color: activeTab === tab ? 'var(--color-brand)' : 'var(--color-text-muted)',
              background: 'none',
              border: 'none',
              borderBottom: activeTab === tab ? '2px solid var(--color-brand)' : '2px solid transparent',
              cursor: 'pointer',
              marginBottom: -1,
            }}
          >
            {t(`entity_tabs.${tab}`)}
          </button>
        ))}
      </div>

      {/* Error state */}
      {isError && (
        <div style={{
          padding: 'var(--space-4)', borderRadius: 'var(--radius-md)',
          background: 'var(--color-danger-pale)', color: 'var(--color-danger)',
          fontSize: 'var(--text-sm)', marginBottom: 'var(--space-4)',
        }}>
          {t('load_error')}
        </div>
      )}

      {/* Loading skeleton */}
      {isLoading && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-3)' }}>
          {Array.from({ length: 3 }).map((_, i) => (
            <div key={i} className="skeleton-line" style={{ height: 56 }} />
          ))}
        </div>
      )}

      {/* Rules list */}
      {!isLoading && !isError && (
        <div style={{
          border: '1px solid var(--color-border)', borderRadius: 'var(--radius-lg)',
          overflow: 'hidden', marginBottom: 'var(--space-4)',
        }}>
          {rules.length === 0 ? (
            <div style={{ padding: 'var(--space-8)', textAlign: 'center' }}>
              <p style={{ fontSize: 'var(--text-sm)', color: 'var(--color-text-muted)' }}>{t('no_rules')}</p>
            </div>
          ) : rules.map((rule, idx) => (
            <div
              key={rule.id}
              style={{
                display: 'flex', alignItems: 'flex-start', gap: 'var(--space-3)',
                padding: 'var(--space-3) var(--space-4)',
                borderBottom: idx < rules.length - 1 ? '1px solid var(--color-gray-100)' : 'none',
                background: 'var(--color-bg)',
              }}
            >
              {/* Active toggle */}
              <button
                type="button"
                onClick={() => void handleToggleActive(rule)}
                title={rule.isActive ? t('active') : t('inactive')}
                style={{
                  width: 10, height: 10, borderRadius: '50%', border: 'none',
                  background: rule.isActive ? 'var(--color-success)' : 'var(--color-gray-300)',
                  cursor: 'pointer', flexShrink: 0, marginTop: 4,
                }}
              />

              {/* Rule info */}
              <div style={{ flex: 1, minWidth: 0 }}>
                <p style={{ fontSize: 'var(--text-sm)', fontWeight: 'var(--font-medium)', color: rule.isActive ? 'var(--color-text)' : 'var(--color-text-muted)' }}>
                  {rule.name}
                </p>
                <p style={{ fontSize: 'var(--text-xs)', color: 'var(--color-text-muted)', marginTop: 2 }}>
                  {t(`triggers.${rule.trigger}`)} · IF {rule.condition.field} {t(`operators.${rule.condition.operator}`)}
                  {rule.condition.value != null ? ` "${String(rule.condition.value)}"` : ''} → {t(`action_types.${rule.action.type}`)}
                  {rule.action.field ? ` (${rule.action.field})` : ''}
                </p>
                <p style={{ fontSize: 'var(--text-xs)', color: 'var(--color-text-muted)', marginTop: 1, fontStyle: 'italic' }}>
                  {rule.errorMessage}
                </p>
              </div>

              {/* Actions */}
              <button
                type="button"
                onClick={() => startEdit(rule)}
                aria-label={`Edit ${rule.name}`}
                style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--color-gray-400)', display: 'flex', padding: 4, flexShrink: 0 }}
              >
                <Pencil size={14} />
              </button>
              <button
                type="button"
                onClick={() => void handleDelete(rule)}
                aria-label={`Delete ${rule.name}`}
                style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--color-gray-300)', display: 'flex', padding: 4, flexShrink: 0 }}
              >
                <Trash2 size={14} />
              </button>
            </div>
          ))}
        </div>
      )}

      {/* Rule form */}
      {showForm ? (
        <div style={{
          border: '1px solid var(--color-border)', borderRadius: 'var(--radius-lg)',
          padding: 'var(--space-5)',
        }}>
          <p style={{ fontSize: 'var(--text-sm)', fontWeight: 'var(--font-medium)', color: 'var(--color-text)', marginBottom: 'var(--space-4)' }}>
            {editingId ? ts('stages.save_changes') : t('add_rule')}
          </p>

          <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-4)' }}>
            {/* Name */}
            <FormField label={t('form.name_label')}>
              <input
                type="text" value={form.name} autoFocus
                onChange={(e) => setField('name', e.target.value)}
                placeholder={t('form.name_placeholder')}
                style={inputStyle}
              />
            </FormField>

            {/* Trigger */}
            <FormField label={t('form.trigger_label')}>
              <select value={form.trigger} onChange={(e) => setField('trigger', e.target.value)} style={{ ...inputStyle, appearance: 'auto' }}>
                {TRIGGERS.map((tr) => (
                  <option key={tr} value={tr}>{t(`triggers.${tr}`)}</option>
                ))}
              </select>
            </FormField>

            {/* Condition row */}
            <div>
              <p style={{ fontSize: 'var(--text-sm)', fontWeight: 'var(--font-medium)', color: 'var(--color-text)', marginBottom: 'var(--space-2)' }}>
                {t('form.condition_label')}
              </p>
              <div style={{ display: 'grid', gridTemplateColumns: showValueInput ? '1fr 1fr 1fr' : '1fr 1fr', gap: 'var(--space-3)' }}>
                <div>
                  <label style={labelStyle}>{t('form.condition_field_label')}</label>
                  <input
                    type="text" value={form.conditionField}
                    onChange={(e) => setField('conditionField', e.target.value)}
                    placeholder={t('form.condition_field_placeholder')}
                    style={{ ...inputStyle, marginTop: 4 }}
                  />
                </div>
                <div>
                  <label style={labelStyle}>{t('form.condition_operator_label')}</label>
                  <select
                    value={form.conditionOperator}
                    onChange={(e) => setField('conditionOperator', e.target.value)}
                    style={{ ...inputStyle, appearance: 'auto', marginTop: 4 }}
                  >
                    {OPERATORS.map((op) => (
                      <option key={op} value={op}>{t(`operators.${op}`)}</option>
                    ))}
                  </select>
                </div>
                {showValueInput && (
                  <div>
                    <label style={labelStyle}>{t('form.condition_value_label')}</label>
                    <input
                      type="text" value={form.conditionValue}
                      onChange={(e) => setField('conditionValue', e.target.value)}
                      placeholder={t('form.condition_value_placeholder')}
                      style={{ ...inputStyle, marginTop: 4 }}
                    />
                  </div>
                )}
              </div>
            </div>

            {/* Action row */}
            <div>
              <p style={{ fontSize: 'var(--text-sm)', fontWeight: 'var(--font-medium)', color: 'var(--color-text)', marginBottom: 'var(--space-2)' }}>
                {t('form.action_label')}
              </p>
              <div style={{ display: 'grid', gridTemplateColumns: showActionField ? '1fr 1fr' : '1fr', gap: 'var(--space-3)' }}>
                <div>
                  <label style={labelStyle}>{t('form.action_type_label')}</label>
                  <select
                    value={form.actionType}
                    onChange={(e) => setField('actionType', e.target.value)}
                    style={{ ...inputStyle, appearance: 'auto', marginTop: 4 }}
                  >
                    {ACTION_TYPES.map((at) => (
                      <option key={at} value={at}>{t(`action_types.${at}`)}</option>
                    ))}
                  </select>
                </div>
                {showActionField && (
                  <div>
                    <label style={labelStyle}>{t('form.action_field_label')}</label>
                    <input
                      type="text" value={form.actionField}
                      onChange={(e) => setField('actionField', e.target.value)}
                      placeholder={t('form.action_field_placeholder')}
                      style={{ ...inputStyle, marginTop: 4 }}
                    />
                  </div>
                )}
              </div>
            </div>

            {/* Error message */}
            <FormField label={t('form.error_message_label')}>
              <input
                type="text" value={form.errorMessage}
                onChange={(e) => setField('errorMessage', e.target.value)}
                placeholder={t('form.error_message_placeholder')}
                style={inputStyle}
              />
            </FormField>
          </div>

          {/* Form actions */}
          <div style={{ display: 'flex', gap: 'var(--space-2)', marginTop: 'var(--space-5)', justifyContent: 'flex-end' }}>
            <button
              type="button"
              onClick={cancelForm}
              style={{
                display: 'flex', alignItems: 'center', gap: 'var(--space-1)',
                padding: 'var(--space-2) var(--space-3)',
                background: 'none', border: '1px solid var(--color-border)',
                borderRadius: 'var(--radius-md)', fontSize: 'var(--text-sm)',
                color: 'var(--color-text-secondary)', cursor: 'pointer',
              }}
            >
              <X size={14} />
              {t('cancel')}
            </button>
            <button
              type="button"
              onClick={() => void handleSave()}
              disabled={isSaving || !form.name.trim() || !form.conditionField.trim() || !form.errorMessage.trim()}
              style={{
                display: 'flex', alignItems: 'center', gap: 'var(--space-1)',
                padding: 'var(--space-2) var(--space-4)',
                background: 'var(--color-brand)', color: 'var(--color-text-inverse)',
                border: 'none', borderRadius: 'var(--radius-md)',
                fontSize: 'var(--text-sm)', fontWeight: 'var(--font-medium)',
                cursor: isSaving ? 'not-allowed' : 'pointer',
                opacity: isSaving ? 0.7 : 1,
              }}
            >
              <Check size={14} />
              {t('save_rule')}
            </button>
          </div>
        </div>
      ) : (
        <button
          type="button"
          onClick={startCreate}
          style={{
            display: 'flex', alignItems: 'center', gap: 'var(--space-2)',
            padding: 'var(--space-2) var(--space-4)',
            background: 'none', border: '1px solid var(--color-border)',
            borderRadius: 'var(--radius-md)', fontSize: 'var(--text-sm)',
            color: 'var(--color-text-secondary)', cursor: 'pointer',
          }}
        >
          <Plus size={14} />
          {t('add_rule')}
        </button>
      )}
    </div>
  );
}

function FormField({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <label style={labelStyle}>{label}</label>
      <div style={{ marginTop: 4 }}>{children}</div>
    </div>
  );
}

const labelStyle: React.CSSProperties = {
  fontSize: 'var(--text-sm)',
  fontWeight: 'var(--font-medium)',
  color: 'var(--color-text)',
  display: 'block',
};

const inputStyle: React.CSSProperties = {
  width: '100%',
  padding: 'var(--space-2) var(--space-3)',
  border: '1px solid var(--color-border)',
  borderRadius: 'var(--radius-md)',
  fontSize: 'var(--text-sm)',
  color: 'var(--color-text)',
  background: 'var(--color-bg)',
  boxSizing: 'border-box',
};
